import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  Renderer2 as Renderer
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { AdvanceEventOutcomes, AdvanceEventOutcomesHistory, isNotNullOrUndefined, System, SystemsService } from "ngx-core-services";
import { switchMap, tap, map, startWith } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { ColumnConfig, ColumnTypeEnum } from "ngx-zcs-components";
import { format } from "date-fns";
import { ActivatedRoute, Router } from "@angular/router";

import { CreateBlockReduxSupport, IReduxBlock } from "../../store/helpers/decorator";
import { requestSystemCurrentEvents, requestSystemHistoricalEvents, selectedSystemUid, SystemsState } from "../../store";
import { selectSystemCurrentEventBySelectedSystem, selectSystemEventsHistoryBySelectedSystem, selectSystemEventsHistoryStatusBySelectedSystem } from "../../store/selectors/events-list.selectors";
import { CreateCustomBlockSizeType, ICustomBlock } from "../decorators";
import { updateEventModelsTranslations } from "../notification-configuration/event-utils"

const labels = {
  currentEventTitle: 'Current Event',
  historicalEventTitle: 'Historical Event',
  actions: 'Actions',
  eventModelName: 'Event Model Name',
  deviceId: 'Device ID',
  open_ts: 'Open Timestamp',
  close_ts: 'Close Timestamp',
  noData: 'No data available',
  noResults: 'No results found',
  of: 'of',
  noResultsForSelectedPeriod: 'No results for selected period',
  noCurrentEvents: 'No current events',
  weakBatteryName: "Weak battery",
  weakBatteryDescription: "The battery is weak",
  batteryLowName: "Battery low",
  batteryLowDescription: "The battery is low",
  gridOvervoltageName: "Grid Overvoltage",
  gridOvervoltageDescription: "The grid voltage (AC) is higher than the limits imposed by regulations.",
  gridUndervoltageName: "Grid Undervoltage",
  gridUndervoltageDescription: "The grid voltage (AC) is lower than the limits imposed by regulations.",
  gridOverfrequencyName: "Grid Overfrequency",
  gridOverfrequencyDescription: "The grid frequency (AC) is higher than the limits imposed by regulations.",
  gridUnderfrequencyName: "Grid Underfrequency",
  gridUnderfrequencyDescription: "The grid frequency (AC) is lower than the limits imposed by regulations.",

  leakageCurrentFaultName: "Leakage Current Fault",
  leakageCurrentFaultDescription: "Leakage current detected, indicating a potential fault.",
  islandingErrorName: "Islanding Error",
  islandingErrorDescription: "The inverter fails to detect that the photovoltaic system is isolated from the grid, posing a potential safety risk.",
  relayDetectionFailureName: "Relay Detection Failure",
  relayDetectionFailureDescription: "Malfunction in detecting the AC relay, which may prevent the circuit from opening or closing when needed.",
  lowInsulationResistanceName: "Low Insulation Resistance",
  lowInsulationResistanceDescription: "Low insulation resistance on the DC side.",
  ctErrorName: "CT Error",
  ctErrorDescription: "Error in the current transformer (CT), preventing accurate current measurement.",
  pvOvervoltageName: "PV Overvoltage",
  pvOvervoltageDescription: "An overvoltage has been detected in the photovoltaic panels, potentially causing damage to modules or the inverter.",
  meterErrorName: "Meter Error",
  meterErrorDescription: "Malfunction of the power meter, preventing accurate monitoring of exchanged energy.",
  batteryLowVoltageShutdownName: "Battery Low Voltage Shutdown",
  batteryLowVoltageShutdownDescription: "Battery is shutting down due to low voltage.",
  acHardwareOvercurrentFaultName: "Output Hardware Overcurrent Fault",
  acHardwareOvercurrentFaultDescription: "Permanent fault due to an overcurrent in the system’s hardware output.",
  busOvervoltageFaultName: "Bus Overvoltage Fault",
  busOvervoltageFaultDescription: "Permanent overvoltage in the system bus, potentially damaging internal components.",
  busHardwareOvervoltageFaultName: "Bus Hardware Overvoltage Fault",
  busHardwareOvervoltageFaultDescription: "Permanent hardware fault caused by an overvoltage in the bus.",
  pvUnevenCurrentFaultName: "PV Uneven Current Fault",
  pvUnevenCurrentFaultDescription: "Uneven current flow detected from the photovoltaic panels, indicating a possible fault.",
  epsBatteryOvercurrentFaultName: "Battery Overcurrent in EPS Mode",
  epsBatteryOvercurrentFaultDescription: "Permanent battery overcurrent while the system is in EPS (Emergency Power Supply) mode.",
  outputInstantaneousOvercurrentFaultName: "Output Instantaneous Overcurrent Fault",
  outputInstantaneousOvercurrentFaultDescription: "A transient overcurrent persists at the output, potentially damaging the inverter.",
  acCurrentUnbalanceFaultName: "Output Current Unbalance Fault",
  acCurrentUnbalanceFaultDescription: "Permanent unbalance in the output current, which could damage the system.",
  inverterSoftStartFailureName: "Inverter Soft Start Failure",
  inverterSoftStartFailureDescription: "Fault in the inverter’s soft start process, which may lead to malfunctions.",
  inputModeSettingFailureName: "Input Mode Setting Failure",
  inputModeSettingFailureDescription: "Permanent error in the input mode setting configuration.",
  inputOvercurrentFaultName: "Input Overcurrent Fault",
  inputOvercurrentFaultDescription: "Permanent fault caused by an overcurrent at the system input.",
  inputHardwareOvercurrentFaultName: "Input Hardware Overcurrent Fault",
  inputHardwareOvercurrentFaultDescription: "Permanent hardware overcurrent at the system input.",
  relayUnbalancedPermanentFailureName: "Relay Permanent Fault",
  relayUnbalancedPermanentFailureDescription: "Permanent fault in the relay, potentially preventing proper system operation.",
  busUnbalanceFaultName: "Bus Unbalance Fault",
  busUnbalanceFaultDescription: "Permanent fault caused by an imbalance in the system bus.",
  spiCommError_DCName: "SPI Communication Error (DC Side)",
  spiCommError_DCDescription: "Problem in the SPI communication inside the inverter.",
  spiCommError_ACName: "SPI Communication Error (AC Side)",
  spiCommError_ACDescription: "Problem in the SPI communication inside the inverter.",
  safetyVerFaultName: "Safety Regulation Version Error",
  safetyVerFaultDescription: "Error in the version of the safety regulation file.",
  sciError_DCName: "SCI Communication Error (DC Side)",
  sciError_DCDescription: "SCI communication issue on the DC side.",
  sciError_ACName: "SCI Communication Error (AC Side)",
  sciError_ACDescription: "SCI communication error on the AC side.",
  sciError_FuseName: "Convergence Board Communication Error",
  sciError_FuseDescription: "Detection of a communication error with the convergence board.",
  inconsistentSoftwareVersionName: "Inconsistent Software Version",
  inconsistentSoftwareVersionDescription: "An inconsistency has been detected between software versions.",
  bms1CommunicatonFaultName: "Battery 1 Communication Error",
  bms1CommunicatonFaultDescription: "Malfunction in communication with battery 1.",
  bms2CommunicatonFaultName: "Battery 2 Communication Error",
  bms2CommunicatonFaultDescription: "Communication error with battery 2.",
  fan1FailureName: "Fan 1 Failure",
  fan1FailureDescription: "Failure of fan 1.",
  fan2FailureName: "Fan 2 Failure",
  fan2FailureDescription: "Failure of fan 2.",
  fan3FailureName: "Fan 3 Failure",
  fan3FailureDescription: "Failure of fan 3.",
  fan4FailureName: "Fan 4 Failure",
  fan4FailureDescription: "Failure of fan 4.",
  fan5FailureName: "Fan 5 Failure",
  fan5FailureDescription: "Failure of fan 5.",
  fan6FailureName: "Fan 6 Failure",
  fan6FailureDescription: "Failure of fan 6.",
  fan7FailureName: "Fan 7 Failure",
  fan7FailureDescription: "Failure of fan 7.",
  bmsOvervoltageProtectionName: "BMS Overvoltage Alarm",
  bmsOvervoltageProtectionDescription: "Overvoltage alarm in the Battery Management System (BMS).",
  bmsUndervoltageProtectionName: "BMS Undervoltage Alarm",
  bmsUndervoltageProtectionDescription: "Undervoltage alarm in the BMS, indicating a critically low battery charge.",
  bmsVerFaultName: "BMS Version Inconsistency",
  bmsVerFaultDescription: "Firmware version discrepancy in the BMS.",
  ZBTS5kSleepModeName: "ZBTS5k Sleep Mode",
  ZBTS5kSleepModeDescription: "The ZBTS5k inverter is in sleep mode.",
  gpmErrorName: "Power Magic Error",
  gpmErrorDescription: "Generic Power Magic event with severity: error.",
  gpmNoticeName: "Power Magic Notice",
  gpmNoticeDescription: "Generic Power Magic event with severity: notice.",
  gpmWarningName: "Power Magic Warning",
  gpmWarningDescription: "Generic Power Magic event with severity: warning.",
};

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-500': 500,
})
@Component({
  selector: 'lib-events-list',
  templateUrl: './events-list.component.html',
  styleUrls: ['./events-list.component.scss'],
})
export class EventsListComponent implements IReduxBlock, ICustomBlock, AfterViewInit {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
    this.updateColumnLabels();
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System | undefined>(undefined);
  @Input() system?: System;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly dateSelection$ = new BehaviorSubject<string>(format(new Date(), 'yyyy-MM-dd'));
  defaultPaginationConfig = {
    pageSize: 5,
    pageSizeOptions: [5, 10, 25],
    length: 100,
  };

  readonly requestSystemCurrentEvent$ = combineLatest([
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this._refresh$,
    this._instanceId$,
  ]).pipe(
    tap(([systemId, forceRefresh, instanceId]) =>
      this.store.dispatch(requestSystemCurrentEvents({ systemId, instanceId, forceRefresh }))
    )
  );

  readonly requestSystemHistoricalEvent$ = combineLatest([
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this._refresh$,
    this._instanceId$,
    this.dateSelection$,
  ]).pipe(
    tap(([systemId, forceRefresh, instanceId, date]) =>
      this.store.dispatch(requestSystemHistoricalEvents({ systemId, instanceId, forceRefresh, range: { from: date, to: date} }))
    )
  );

  readonly systemCurrentEvent$ = this._instanceId$.pipe(
    switchMap((instanceId) =>
      this.store.select(selectSystemCurrentEventBySelectedSystem(instanceId))
    ),
    map((events) => events ? events : null),
  );

  readonly systemHistoricalEvent$ = this._instanceId$.pipe(
    switchMap((instanceId) =>
      this.store.select(selectSystemEventsHistoryBySelectedSystem(instanceId))
    ),
    map((events) => events ? events : null),
  );

  readonly systemHistoricalEventPending$ = this._instanceId$.pipe(
    switchMap((instanceId) =>
      this.store.select(selectSystemEventsHistoryStatusBySelectedSystem(instanceId))
    ),
    map((status) => status === 'pending'),
    startWith(false),
    isNotNullOrUndefined(),
  );

  eventModelsTranslations: ReturnType<typeof updateEventModelsTranslations>;

  historicalColumns: ColumnConfig[] = [];
  columns: ColumnConfig[] = [];

  constructor(
    readonly store: Store<SystemsState>,
    readonly el: ElementRef,
    readonly elementRef: ElementRef,
    readonly renderer: Renderer,
    readonly route: ActivatedRoute,
    readonly router: Router,
    readonly systemsService: SystemsService,
  ) {
    this.eventModelsTranslations = updateEventModelsTranslations(this.labels);
  }

  ngAfterViewInit(): void {
    this.updateColumnLabels();
  }

  updateColumnLabels(): void {
    const updatedColumns : ColumnConfig[]= [
      {
        columnDef: 'eventModelName',
        header: this.labels.eventModelName,
        cell: (event: AdvanceEventOutcomes) => {
          const translation = this.eventModelsTranslations[event.eventModelName];
          return translation ? translation.translateName : event.eventModelName;
        },
        type: ColumnTypeEnum.Text,
        sortable: true,
      },
      {
        columnDef: 'deviceId',
        header: this.labels.deviceId,
        cell: (event: AdvanceEventOutcomes) => event.deviceId,
        type: ColumnTypeEnum.Text,
        sortable: true,
      },
      {
        columnDef: 'open_ts',
        header: this.labels.open_ts,
        cell: (event: AdvanceEventOutcomes) => new Date(+event.open_ts * 1000),
        type: ColumnTypeEnum.Date,
        format: 'dd/MM/yyyy HH:mm',
        sortable: true,
      },
    ];

    const updatedHistoricalColumns: ColumnConfig[] = [
      {
        columnDef: 'eventModelName',
        header: this.labels.eventModelName,
        cell: (event: AdvanceEventOutcomesHistory) => {
          const translation = this.eventModelsTranslations[event.eventModelName];
          return translation ? translation.translateName : event.eventModelName;
        },
        type: ColumnTypeEnum.Text,
        sortable: true,
      },
      {
        columnDef: 'deviceId',
        header: this.labels.deviceId,
        cell: (event: AdvanceEventOutcomesHistory) => event.deviceId,
        type: ColumnTypeEnum.Text,
        sortable: true,
      },
      {
        columnDef: 'open_ts',
        header: this.labels.open_ts,
        cell: (event: AdvanceEventOutcomesHistory) => new Date(+event.open_ts * 1000),
        cellHidden: (event: AdvanceEventOutcomesHistory) => !event.open_ts,
        type: ColumnTypeEnum.Date,
        format: 'dd/MM/yyyy hh:mm',
        sortable: true,
      },
      {
        columnDef: 'close_ts',
        header: this.labels.close_ts,
        cell: (event: AdvanceEventOutcomesHistory) => new Date(+event.close_ts * 1000),
        cellHidden: (event: AdvanceEventOutcomesHistory) => !event.close_ts,
        type: ColumnTypeEnum.Date,
        format: 'dd/MM/yyyy hh:mm',
        sortable: true,
      },
    ];

    // Assign completely new references to trigger Angular change detection
    this.columns = [...updatedColumns];
    this.historicalColumns = [...updatedHistoricalColumns];
  }
}
